import { ApolloQueryResult } from '@apollo/client';
import { Box, Flex, Spacer, Heading } from '@chakra-ui/react';
import React from 'react';

import { PortfolioChartComponentTypeSelectButtons } from './PortfolioChartComponentTypeSelectButtons';
import { PortfolioChartDatePicker } from './PortfolioChartDatePicker';
import {
  ChartTimePeriod,
  onSelectDateRange,
  onSelectDateRangeForOrganization,
  PortfolioChartComponentType,
} from './portfolioChartUtils';
import { PortfolioTimePeriodSelectButtons } from './PortfolioTimePeriodSelectButtons';
import { colors } from 'packages/common-frontend/colors';
import { HighlightBox } from 'packages/common-frontend/components/HighlightBox';
import { IsolatedMultiSelect } from 'packages/common-frontend/components/MultiSelect';

// Credit facility-Level Chart Templates

interface PortfolioChartTemplateForCreditFacilityProps<T> {
  creditFacilityId: string;
  chartTitle: string;
  refetch: (variables: { [key: string]: any }) => Promise<ApolloQueryResult<T>>;
  refetchVariables?: { [key: string]: any };
  chartDataDateRange: { chartDataStartDate: Date; chartDataEndDate: Date };
  setChartDataDateRange: (
    value: React.SetStateAction<{
      chartDataStartDate: Date;
      chartDataEndDate: Date;
    }>,
  ) => void;
  selectedTimePeriod: ChartTimePeriod;
  onSelectTimePeriod: (timePeriod: ChartTimePeriod) => void;
  children: (React.ReactElement | null)[];
}

// Takes 2 children: 1 for the graph and 1 for the details
export const PortfolioChartWithCardsTemplateForCreditFacility = <
  T extends { __typename?: 'Query' | undefined }
>() => {
  const Instance: React.FC<PortfolioChartTemplateForCreditFacilityProps<T>> = ({
    creditFacilityId,
    chartTitle,
    refetch,
    refetchVariables,
    chartDataDateRange,
    setChartDataDateRange,
    selectedTimePeriod,
    onSelectTimePeriod,
    children,
  }) => {
    return (
      <Box mb="40px">
        <Heading as="h3" fontSize="24px" mb="24px">
          {chartTitle}
        </Heading>
        <HighlightBox backgroundColor={colors.millet[50]}>
          <Flex
            backgroundColor="white"
            borderRadius="8px"
            borderWidth="1px"
            borderColor={colors.gray[200]}
            padding="40px"
          >
            <Box width="70%">
              <Flex mb="40px">
                <PortfolioChartDatePicker
                  onSelectDateRange={(
                    newDateRange: [Date | null, Date | null],
                  ) =>
                    onSelectDateRange<T>({
                      newDateRange,
                      refetch,
                      refetchVariables,
                      creditFacilityId,
                      setChartDataDateRange,
                    })
                  }
                  currentDateRange={chartDataDateRange}
                />
                <Spacer />
                <PortfolioTimePeriodSelectButtons
                  currentSelectedTimePeriod={selectedTimePeriod}
                  onSelectTimePeriod={onSelectTimePeriod}
                />
              </Flex>

              {children[0]}
            </Box>
            <Spacer />
            {children[1]}
          </Flex>
        </HighlightBox>
      </Box>
    );
  };

  return Instance;
};

// Takes 2 children: 1 for the graph and 1 for the details
export const PortfolioChartWithTableTemplateForCreditFacility = <
  T extends { __typename?: 'Query' | undefined }
>() => {
  const Instance: React.FC<PortfolioChartTemplateForCreditFacilityProps<T>> = ({
    creditFacilityId,
    chartTitle,
    refetch,
    chartDataDateRange,
    setChartDataDateRange,
    selectedTimePeriod,
    onSelectTimePeriod,
    children,
  }) => {
    return (
      <Box mb="40px">
        <Heading as="h3" fontSize="24px" mb="24px">
          {chartTitle}
        </Heading>
        <HighlightBox backgroundColor={colors.millet[50]}>
          <Box
            backgroundColor="white"
            borderRadius="8px"
            borderWidth="1px"
            borderColor={colors.gray[200]}
            padding="40px"
          >
            <Flex mb="40px">
              <PortfolioChartDatePicker
                onSelectDateRange={(newDateRange: [Date | null, Date | null]) =>
                  onSelectDateRange<T>({
                    newDateRange,
                    refetch,
                    creditFacilityId,
                    setChartDataDateRange,
                  })
                }
                currentDateRange={chartDataDateRange}
              />
              <Spacer />
              <PortfolioTimePeriodSelectButtons
                currentSelectedTimePeriod={selectedTimePeriod}
                onSelectTimePeriod={onSelectTimePeriod}
              />
            </Flex>

            {children[0]}
            {children[1]}
          </Box>
        </HighlightBox>
      </Box>
    );
  };

  return Instance;
};

// Organization-Level Chart Templates

interface PortfolioChartTemplateForOrganizationProps<T>
  extends Omit<
    PortfolioChartTemplateForCreditFacilityProps<T>,
    'creditFacilityId' | 'chartTitle'
  > {
  organizationId: string;
  selectedCreditFacilityIds?: string[];
  onSelectCreditFacilityIds?: (creditFacilityIds: string[]) => void;
  initialCreditFacilitySelectOptions?: { value: string; label: string }[];
  selectedChartComponentType?: PortfolioChartComponentType;
  onSelectChartComponentType?: (
    chartComponentType: PortfolioChartComponentType,
  ) => void;
}

// Takes 2 children: 1 for the graph and 1 for the details
// Includes credit facility isolated multiselect
export const PortfolioChartWithCardsTemplateForOrganization = <
  T extends { __typename?: 'Query' | undefined }
>() => {
  const Instance: React.FC<PortfolioChartTemplateForOrganizationProps<T>> = ({
    organizationId,
    refetch,
    chartDataDateRange,
    setChartDataDateRange,
    selectedTimePeriod,
    onSelectTimePeriod,
    selectedCreditFacilityIds,
    onSelectCreditFacilityIds,
    selectedChartComponentType,
    onSelectChartComponentType,
    initialCreditFacilitySelectOptions,
    children,
  }) => {
    return (
      <>
        <Flex mb="40px">
          {selectedCreditFacilityIds &&
            onSelectCreditFacilityIds &&
            initialCreditFacilitySelectOptions && (
              <Box mr="32px">
                <IsolatedMultiSelect
                  options={initialCreditFacilitySelectOptions}
                  initialSelections={selectedCreditFacilityIds}
                  onApplyOptions={onSelectCreditFacilityIds}
                  optionsHeader="Credit Facilities"
                  titleText={`Credit Facility | ${selectedCreditFacilityIds.length}`}
                  applyButtonText="Apply filters"
                  colorScheme="aurora"
                  borderWidth="2px"
                  width="250px"
                />
              </Box>
            )}
          <PortfolioChartDatePicker
            onSelectDateRange={(newDateRange: [Date | null, Date | null]) =>
              onSelectDateRangeForOrganization<T>({
                newDateRange,
                refetch,
                organizationId,
                setChartDataDateRange,
              })
            }
            currentDateRange={chartDataDateRange}
            width="300px"
          />
          <PortfolioTimePeriodSelectButtons
            currentSelectedTimePeriod={selectedTimePeriod}
            onSelectTimePeriod={onSelectTimePeriod}
          />
          <Spacer />
          {selectedChartComponentType && onSelectChartComponentType && (
            <PortfolioChartComponentTypeSelectButtons
              currentSelectedChartComponentType={selectedChartComponentType}
              onSelectChartComponentType={onSelectChartComponentType}
            />
          )}
        </Flex>
        <Flex>
          <Box width="70%">{children[0]}</Box>
          <Spacer />
          {children[1]}
        </Flex>
      </>
    );
  };

  return Instance;
};
